import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import {
  Button,
  Card,
  CardContent,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Radio,
} from '@material-ui/core';

import * as events from '../../../events';
import * as actions from '../../../state';
import { getAvatar } from '../../../utils';
import styles from './styles.module.css';

const messages = defineMessages({
  LOGO: {
    id: 'LOGO',
    defaultMessage: 'Logo',
  },
  CONTINUE: {
    id: 'CONTINUE',
    defaultMessage: 'Continue',
  },
  CONNECT_ANOTHER_ACCOUNT: {
    id: 'CONNECT_ANOTHER_ACCOUNT',
    defaultMessage: 'Connect a different account',
  },
  RETURN_TO_LOGIN: {
    id: 'RETURN_TO_LOGIN',
    defaultMessage: 'Return to login',
  },
  SELECT_ACCOUNT: {
    id: 'SELECT_ACCOUNT',
    defaultMessage: 'Select an account to log in with',
  },
  RECENTLY_USED_ACCOUNT: {
    id: 'RECENTLY_USED_ACCOUNT',
    defaultMessage: 'Recently used account',
  },
  ALL_ACCOUNTS: {
    id: 'ALL_ACCOUNTS',
    defaultMessage: 'All accounts',
  },
});

function SubscriberSelect({
  idtoken: idToken,
  intl,
  vendor,
  subscriber_list: subscriberList,
}) {
  const dispatch = useDispatch();
  const loginImage = useSelector(actions.selectLoginImage);
  const [selectedSubscriber, setSelectedSubscriber] = useState('');

  let mostRecent;
  subscriberList.forEach((subscriber) => {
    if (!mostRecent || mostRecent.modified < subscriber.modified) {
      mostRecent = subscriber;
    }
  });

  function subscriberSelected(e) {
    e.preventDefault();
    dispatch(events.ssoLoginUser(
      idToken,
      vendor,
      selectedSubscriber,
    ));
  }

  return (
    <div className={`${styles.loginCard} animate__animated animate__fadeIn`}>
      <Card className={styles.card}>
        <div className={styles.media}>
          <img
            src={loginImage}
            title={intl.formatMessage({ ...messages.LOGO })}
            alt={intl.formatMessage({ ...messages.LOGO })}
          />
        </div>

        <CardContent className={styles.content} classes={{ root: styles.loginFormCard }}>
          <div className={styles.contentHelptext}>
            <FormattedMessage {...messages.SELECT_ACCOUNT} />
          </div>

          <form onSubmit={subscriberSelected} noValidate style={{ marginTop: '7px' }}>
            <div className={styles.accountHeader}>
              <FormattedMessage {...messages.RECENTLY_USED_ACCOUNT} />
            </div>

            <List dense>
              <ListItem key={mostRecent?.uid} button onClick={() => setSelectedSubscriber(mostRecent?.uid)}>
                <ListItemAvatar>
                  {getAvatar(mostRecent, styles)}
                </ListItemAvatar>
                <ListItemText primary={mostRecent?.name} secondary={mostRecent?.uid} />
                <ListItemSecondaryAction>
                  <Radio
                    // eslint-disable-next-line eqeqeq
                    checked={selectedSubscriber === mostRecent?.uid}
                    onClick={() => setSelectedSubscriber(mostRecent?.uid)}
                    inputProps={{ 'aria-labelledby': `checkbox-list-secondary-label-${mostRecent?.uid}` }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>

            {subscriberList.length ? (
              <>
                <div className={styles.accountHeader}>
                  <FormattedMessage {...messages.ALL_ACCOUNTS} />
                </div>
                <List dense style={{ maxHeight: '35vh', overflowY: 'scroll' }}>
                  {subscriberList.map((subscriber) => {
                    const labelId = `checkbox-list-secondary-label-${subscriber.uid}`;
                    // eslint-disable-next-line no-param-reassign
                    subscriber.type = 'subscriber';

                    return (
                      <ListItem key={subscriber.uid} button onClick={() => setSelectedSubscriber(subscriber.uid)}>
                        <ListItemAvatar>
                          {getAvatar(subscriber, styles)}
                        </ListItemAvatar>
                        <ListItemText primary={subscriber.name} secondary={subscriber.uid} />
                        <ListItemSecondaryAction>
                          <Radio
                            // eslint-disable-next-line eqeqeq
                            checked={selectedSubscriber === subscriber.uid}
                            onClick={() => setSelectedSubscriber(subscriber.uid)}
                            inputProps={{ 'aria-labelledby': labelId }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </>
            ) : null}

            <Button
              fullWidth
              size="large"
              type="submit"
              color="primary"
              variant="contained"
              style={{
                marginTop: '6px !important',
                marginBottom: '24px !important',
              }}
              disabled={!selectedSubscriber}
              className={selectedSubscriber ? styles.loginBtn : styles.loginBtnDisabled}
            >
              <FormattedMessage {...messages.CONTINUE} />
            </Button>

            <div className={styles.connectAccountContainer}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link to="#" onClick={() => dispatch(events.ssoEnroll(idToken, vendor))} underline="hover">
                <FormattedMessage {...messages.CONNECT_ANOTHER_ACCOUNT} />
              </Link>
            </div>

            <div className={styles.connectAccountContainer}>
              <Link to="/" onClick={() => dispatch(actions.showLoginFormPage())} underline="hover">
                <FormattedMessage {...messages.RETURN_TO_LOGIN} />
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

SubscriberSelect.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  subscriber_list: [],
};

SubscriberSelect.propTypes = {
  idtoken: PropTypes.string.isRequired,
  subscriber_list: PropTypes.arrayOf(PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
  })),
};

export default injectIntl(SubscriberSelect);
